// src/hooks/usePermissions.ts - VERSION SIMPLIFIÉE sans modules par type de business

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { type RolePermissions, SUPER_ADMIN_FULL_PERMISSIONS } from '../types/user';
import { applyBusinessFeaturePermissionGates } from '../utils/rolePermissionsBusinessGates';
import { useAppTranslation } from './useAppTranslation';
import { useRoleProfile } from './useRoleProfiles';

// Interface pour les permissions adaptatives
// Les permissions sont maintenant uniquement basées sur les profils de rôle
export type AdaptivePermissions = RolePermissions;

const adaptPermissions = applyBusinessFeaturePermissionGates;

// Hook principal pour les permissions adaptatives
export const usePermissions = (): AdaptivePermissions | null => {
  const authContext = useAuth();
  
  // ✅ IMPORTANT : Toujours appeler les hooks en premier, avant les early returns
  const { userProfile, currentBusiness, isSuperAdmin, isCSM } = authContext || {};
  
  // ✅ Utiliser roleProfileIds[businessId] pour le business actuel
  const roleProfileIdForBusiness = currentBusiness 
    ? userProfile?.roleProfileIds?.[currentBusiness.id]
    : undefined;
  
  // ✅ Hook toujours appelé, même si undefined (le hook gère ce cas)
  const { data: roleProfile, isLoading, error } = useRoleProfile(roleProfileIdForBusiness);
  
  // Vérification que le contexte est bien chargé
  if (!authContext) {
    return null;
  }
  
  if (!userProfile) {
    return null;
  }

  // Super Admin / CSM dans l’espace sans business client : toutes les permissions
  if ((isSuperAdmin || isCSM) && !currentBusiness) {
    return adaptPermissions(SUPER_ADMIN_FULL_PERMISSIONS, null);
  }
  
  // Business obligatoire pour tous (super admin inclus quand il est dans un business)
  if (!currentBusiness) {
    return null;
  }

  // Vérifier que l'utilisateur a bien accès au business actuel
  // Super admin et CSM ont accès à tout commerce (impersonation) ; les autres : businessIds
  if (!isSuperAdmin && !isCSM && !userProfile.businessIds.includes(currentBusiness.id)) {
    console.warn('Utilisateur tente d\'accéder à un business non autorisé:', currentBusiness.id);
    return null;
  }

  // Super admin ou CSM dans un business : ignore le profil de rôle — accès complet (y compris plans pour super admin / CSM pilotage)
  if ((isSuperAdmin || isCSM) && currentBusiness) {
    return adaptPermissions(SUPER_ADMIN_FULL_PERMISSIONS, currentBusiness, {
      bypassPaymentPlansContract: true,
    });
  }

  // Pas de profil de rôle pour ce business : pas d’accès (super admin / CSM déjà sortis plus haut)
  if (!roleProfileIdForBusiness) {
    console.warn('Utilisateur sans roleProfileId pour le business:', userProfile.id, currentBusiness.id);
    return null;
  }

  if (isLoading) {
    return null; // En cours de chargement
  }

  if (error || !roleProfile) {
    console.warn('Profil de rôle introuvable:', roleProfileIdForBusiness, error);
    return null;
  }

  // Adapter les permissions selon les modules activés du business
  return adaptPermissions(roleProfile.permissions, currentBusiness);
};

// Hook pour vérifier une permission spécifique
export const useHasPermission = (permission: keyof AdaptivePermissions): boolean => {
  const permissions = usePermissions();
  const hasPermission = permissions ? permissions[permission] : false;
  return hasPermission;
};


// Hook pour obtenir la terminologie (termes fixes pour tous les business, i18n)
export const useBusinessTerminology = () => {
  const { t, i18n } = useAppTranslation('common');
  return useMemo(
    () => ({
      business: t('terminology.business'),
      client: t('terminology.client'),
      employee: t('terminology.employee'),
      service: t('terminology.service'),
      appointment: t('terminology.appointment'),
      transaction: t('terminology.transaction'),
      supplier: t('terminology.supplier'),
      delivery: t('terminology.delivery'),
      accounting: t('terminology.accounting'),

      clients: t('terminology.clients'),
      employees: t('terminology.employees'),
      services: t('terminology.services'),
      appointments: t('terminology.appointments'),
      transactions: t('terminology.transactions'),
      suppliers: t('terminology.suppliers'),
      deliveries: t('terminology.deliveries'),
    }),
    [t, i18n.language],
  );
};

// Hook pour vérifier si la gestion fournisseurs est disponible
export const useSuppliersEnabled = (): boolean => {
  const permissions = usePermissions();
  return permissions?.canViewSuppliers || permissions?.canCreateSuppliers || permissions?.canUpdateSuppliers || permissions?.canDeleteSuppliers || false;
};

// Hook pour vérifier si les inventaires physiques sont disponibles
export const useInventoryAuditEnabled = (): boolean => {
  const permissions = usePermissions();
  return permissions?.canViewInventoryAudit || permissions?.canCreateInventoryAudit || false;
};

// Hook pour vérifier les permissions spécifiques d'inventaire
export const useInventoryAuditPermissions = () => {
  const permissions = usePermissions();
  const { getInventoryAuditPermissions } = require('../utils/permissionHelpers');
  const auditPerms = getInventoryAuditPermissions(permissions);
  
  return {
    canView: auditPerms.canView,
    canCreate: auditPerms.canCreate,
    canUpdate: auditPerms.canUpdate,
    canDelete: auditPerms.canDelete,
    canManage: auditPerms.canUpdate || auditPerms.canDelete, // Rétrocompatibilité
    canCount: permissions?.canCountInventory || false,
    canValidate: permissions?.canValidateInventory || false,
    canViewReports: permissions?.canViewInventoryAuditReports || false,
  };
};

// Ré-export des types pour faciliter les imports
export type { RolePermissions } from '../types/user';